import { ZuruflexApiError } from './errors.js';
import { openEventStream } from './stream.js';
/** Mirrors MAX_UPLOAD_BYTES on the API, which rejects anything larger. */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
export class ZuruflexClient {
    baseUrl;
    options;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/$/, '');
        this.options = options;
    }
    currentLocale() {
        const { locale } = this.options;
        return typeof locale === 'function' ? locale() : locale;
    }
    currentToken() {
        const { token } = this.options;
        return typeof token === 'function' ? token() : token;
    }
    /*
     * ── Sign-in ────────────────────────────────────────────────
     *
     * These go through `send` rather than `request`, so a 401 from one of
     * them is returned as-is instead of triggering the refresh-and-retry in
     * `request`. They authenticate with what is in their own body — a
     * password, a Google id token, a refresh token — so a newer access token
     * would not change the answer, and retrying a rejected password is
     * nobody's idea of helpful.
     *
     * For `refreshSession` it is more than pointless. `request` answers a
     * 401 by calling `onUnauthorized`, which is the session's own refresh; a
     * refresh already in flight returns its in-flight promise, and that
     * promise is the one waiting on this very call. The two await each other
     * and neither ever settles: an expired refresh token used to hang the
     * dashboard on its loading screen instead of returning it to sign-in.
     */
    /** Which methods this deployment offers, so a UI can hide the rest. */
    authConfig() {
        return this.request('GET', '/v1/auth/config');
    }
    staffSignIn(email, password) {
        return this.send('POST', '/v1/auth/staff/sign-in', { email, password });
    }
    staffGoogleSignIn(credential) {
        return this.send('POST', '/v1/auth/staff/google', credential);
    }
    partnerSignIn(email, password) {
        return this.send('POST', '/v1/auth/partner/sign-in', { email, password });
    }
    partnerSignUp(input) {
        return this.send('POST', '/v1/auth/partner/sign-up', input);
    }
    /**
     * Signs in with Google, or creates the account when `signUp` is supplied.
     * Without it, an unknown address comes back NOT_FOUND — the portal's cue
     * to show the sign-up form rather than an error.
     */
    partnerGoogleSignIn(credential, signUp) {
        return this.send('POST', '/v1/auth/partner/google', { ...credential, signUp });
    }
    /** Sign in with Apple; the same contract as `partnerGoogleSignIn`. */
    partnerAppleSignIn(credential, signUp) {
        return this.send('POST', '/v1/auth/partner/apple', { ...credential, signUp });
    }
    customerSignIn(email, password) {
        return this.send('POST', '/v1/auth/customer/sign-in', { email, password });
    }
    customerSignUp(input) {
        return this.send('POST', '/v1/auth/customer/sign-up', input);
    }
    /**
     * Starts phone sign-in: a six-digit code goes to the number's WhatsApp.
     *
     * The way into an account that is only a phone number — the record the
     * concierge makes on a first message, which holds the hires and has no
     * password to reset. Every well-formed number is accepted, known or not,
     * so this answer never says who already has an account.
     *
     * @returns the number as the server read it, which is what the next
     *          screen should show: a local `0788…` is Rwandan in Kigali and
     *          Ghanaian in Accra, and the browser has no way to know which.
     */
    startPhoneSignIn(phone, locale) {
        return this.send('POST', '/v1/auth/customer/phone/start', { phone, locale });
    }
    /** Spends the code. Creates the account when the number has none. */
    verifyPhoneSignIn(phone, code, locale) {
        return this.send('POST', '/v1/auth/customer/phone/verify', { phone, code, locale });
    }
    /**
     * Proves a number from inside a session, and pulls in what is behind it.
     *
     * The code comes from `startPhoneSignIn` — it is the same act — and is
     * spent here instead when somebody is already signed in. A number on a
     * record the concierge made is folded into this account with its hires;
     * a number on an account that can sign in for itself is refused.
     */
    claimPhone(phone, code) {
        return this.send('POST', '/v1/auth/customer/phone/claim', { phone, code });
    }
    /**
     * Signs in with Google, creating the account if there is none.
     *
     * No sign-up form to come back for, unlike the partner equivalent:
     * Google supplies a verified address and a name, which is all a customer
     * account holds. Pass `create: false` to ask whether an account exists
     * without making one — the answer for an unknown address is NOT_FOUND.
     */
    customerGoogleSignIn(credential, options = {}) {
        return this.send('POST', '/v1/auth/customer/google', { ...credential, ...options });
    }
    /** Sign in with Apple; the same contract as `customerGoogleSignIn`. */
    customerAppleSignIn(credential, options = {}) {
        return this.send('POST', '/v1/auth/customer/apple', { ...credential, ...options });
    }
    /**
     * Asks for a reset link. Resolves whether or not the address is on file —
     * a UI that branched on the answer would be an account-existence oracle,
     * and there is nothing here for it to branch on.
     */
    requestPasswordReset(email, locale) {
        return this.send('POST', '/v1/auth/customer/forgot-password', { email, locale });
    }
    /** Spends a reset link. Signs every other device out, and this one in. */
    resetPassword(token, newPassword) {
        return this.send('POST', '/v1/auth/customer/reset-password', { token, newPassword });
    }
    /** Spends a verification link. Returns the account, never a session. */
    verifyEmail(token) {
        return this.send('POST', '/v1/auth/customer/verify-email', { token });
    }
    /** Another verification email. Silent, as `requestPasswordReset` is. */
    resendVerification(email, locale) {
        return this.send('POST', '/v1/auth/customer/resend-verification', { email, locale });
    }
    setCustomerPassword(newPassword, currentPassword) {
        return this.request('POST', '/v1/auth/customer/password', { newPassword, currentPassword });
    }
    refreshSession(refreshToken) {
        return this.send('POST', '/v1/auth/refresh', { refreshToken });
    }
    signOut(refreshToken) {
        return this.send('POST', '/v1/auth/sign-out', { refreshToken });
    }
    me() {
        return this.request('GET', '/v1/auth/me');
    }
    setPartnerPassword(newPassword, currentPassword) {
        return this.request('POST', '/v1/auth/partner/password', { newPassword, currentPassword });
    }
    /**
     * The owner of the car accepting or turning down a reservation.
     *
     * Accepting moves it to AWAITING_PAYMENT and the concierge asks the
     * customer to pay over WhatsApp; declining cancels it and tells them
     * why, so `reason` is shown to a real person and should read like it.
     */
    decideBooking(reference, input) {
        return this.request('POST', `/v1/bookings/${encodeURIComponent(reference)}/decision`, input);
    }
    /**
     * One customer's bookings that are still in play, soonest first.
     *
     * Narrower than `listBookings` on purpose — the concierge greets a
     * customer with the car they already have, and has no business seeing
     * anyone else's.
     */
    liveBookings(customerId) {
        return this.request('GET', `/v1/customers/${encodeURIComponent(customerId)}/bookings/live`);
    }
    /**
     * Who a WhatsApp number belongs to, when it belongs to a partner.
     *
     * The concierge asks before treating a message as a booking enquiry: a
     * car owner replying "YES" to a reservation must not be read as a
     * customer confirming a hire of their own.
     */
    partnerForPhone(phone) {
        return this.request('GET', `/v1/partners/by-phone${toQueryString({ phone })}`);
    }
    /**
     * A partner accepting or declining over WhatsApp.
     *
     * Separate from `decideBooking`, which authenticates a portal session.
     * Here the phone number is the credential, so the API re-checks that it
     * really owns the booking rather than trusting the concierge.
     */
    decideBookingByPhone(reference, input) {
        return this.request('POST', `/v1/bookings/${encodeURIComponent(reference)}/decision/by-phone`, input);
    }
    // ── Payments ───────────────────────────────────────────────
    /** What to collect now, per the operator's settings for that branch. */
    amountDue(reference) {
        return this.request('GET', `/v1/payments/${encodeURIComponent(reference)}/due`);
    }
    /**
     * The customer says they sent mobile money by hand. Recorded for staff
     * to check, and holds the car; it never marks anything paid.
     */
    claimPayment(reference, note) {
        return this.request('POST', `/v1/payments/${encodeURIComponent(reference)}/claim`, { note });
    }
    /**
     * Pushes a mobile-money prompt to the customer's handset.
     *
     * Idempotent per booking — a retried message reuses the attempt already
     * in flight rather than prompting twice.
     */
    collectPayment(reference, phone) {
        return this.request('POST', `/v1/payments/${encodeURIComponent(reference)}/collect`, { phone });
    }
    paymentStatus(reference) {
        return this.request('GET', `/v1/payments/${encodeURIComponent(reference)}`);
    }
    // ── Feature flags ──────────────────────────────────────────
    /**
     * Every flag `surface` reads, evaluated for whoever this client is signed
     * in as. `anonymousId` keeps a signed-out install in one place in a
     * percentage rollout.
     */
    evaluateFlags(surface, anonymousId) {
        return this.request('GET', `/v1/flags/evaluate${toQueryString({ surface })}`, undefined, anonymousId ? { 'x-anon-id': anonymousId } : undefined);
    }
    /**
     * The visit behind a WhatsApp tap, filed under the code at the foot of
     * the pre-filled message so the concierge can find it when the message
     * lands. Public: most of the people tapping are signed out.
     */
    recordWhatsappVisit(visit) {
        return this.request('POST', '/v1/whatsapp-visits', visit);
    }
    listFlags() {
        return this.request('GET', '/v1/flags');
    }
    getFlag(key) {
        return this.request('GET', `/v1/flags/${encodeURIComponent(key)}`);
    }
    // ── Managed content ──────────────────────────────────────────
    /** One published page. 404 when it is not live — callers fall back to what the code ships. */
    contentPage(slug) {
        return this.request('GET', `/v1/content/${encodeURIComponent(slug)}`);
    }
    /** The published pages, for the footer, the help hub and the sitemap. */
    contentIndex(kind) {
        return this.request('GET', `/v1/content${toQueryString({ kind })}`);
    }
    contentAdminList() {
        return this.request('GET', '/v1/content/admin');
    }
    contentAdminGet(slug) {
        return this.request('GET', `/v1/content/admin/${encodeURIComponent(slug)}`);
    }
    createContent(input) {
        return this.request('POST', '/v1/content/admin', input);
    }
    /** Saves the working copy only. Refused with 409 if somebody saved since `expectedVersion`. */
    saveContentDraft(slug, input) {
        return this.request('PUT', `/v1/content/admin/${encodeURIComponent(slug)}/draft`, input);
    }
    discardContentDraft(slug, expectedVersion) {
        return this.request('POST', `/v1/content/admin/${encodeURIComponent(slug)}/discard`, {
            expectedVersion,
        });
    }
    /** Owners only. Raises the published version. */
    publishContent(slug, expectedVersion) {
        return this.request('POST', `/v1/content/admin/${encodeURIComponent(slug)}/publish`, {
            expectedVersion,
        });
    }
    unpublishContent(slug, expectedVersion) {
        return this.request('POST', `/v1/content/admin/${encodeURIComponent(slug)}/unpublish`, {
            expectedVersion,
        });
    }
    deleteContent(slug) {
        return this.request('DELETE', `/v1/content/admin/${encodeURIComponent(slug)}`);
    }
    /** A suggestion from the writing model. Nothing is stored; the editor shows it for review. */
    writeContent(input) {
        return this.request('POST', '/v1/content/admin/write', input);
    }
    /** Images and PDFs already uploaded for pages, newest first. */
    contentAssets() {
        return this.request('GET', '/v1/content/admin/assets');
    }
    contentRevisions(slug) {
        return this.request('GET', `/v1/content/admin/${encodeURIComponent(slug)}/revisions`);
    }
    contentRevision(slug, version) {
        return this.request('GET', `/v1/content/admin/${encodeURIComponent(slug)}/revisions/${version}`);
    }
    /** Puts an older version back as the working copy. A draft: an owner still publishes it. */
    restoreContentRevision(slug, version, expectedVersion) {
        return this.request('POST', `/v1/content/admin/${encodeURIComponent(slug)}/revisions/${version}/restore`, { expectedVersion });
    }
    /** Owners only. The working copy goes live at `at`; editing it first cancels this. */
    scheduleContent(slug, at, expectedVersion) {
        return this.request('POST', `/v1/content/admin/${encodeURIComponent(slug)}/schedule`, {
            at,
            expectedVersion,
        });
    }
    unscheduleContent(slug, expectedVersion) {
        return this.request('POST', `/v1/content/admin/${encodeURIComponent(slug)}/unschedule`, {
            expectedVersion,
        });
    }
    contentHistory(slug) {
        return this.request('GET', `/v1/content/admin/${encodeURIComponent(slug)}/history`);
    }
    /**
     * Uploads an image or PDF for a managed page and returns the public URL
     * to put in it: presign, PUT the bytes straight to S3, hand back the URL.
     */
    async uploadContentAsset(file) {
        const presigned = await this.request('POST', '/v1/uploads/presign', {
            kind: 'cms-asset',
            filename: file.name,
            contentType: file.type,
            sizeBytes: file.size,
        });
        const doFetch = this.options.fetch ?? globalThis.fetch;
        const upload = await doFetch(presigned.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
        });
        if (!upload.ok || !presigned.publicUrl) {
            throw new ZuruflexApiError(upload.status || 503, {
                code: 'SERVICE_UNAVAILABLE',
                message: `Uploading ${file.name} failed`,
            });
        }
        return { url: presigned.publicUrl, key: presigned.key };
    }
    /** Refused with 409 if somebody else saved since `expectedVersion`. */
    saveFlag(key, state, expectedVersion) {
        return this.request('PUT', `/v1/flags/${encodeURIComponent(key)}`, { state, expectedVersion });
    }
    /**
     * Flips only the master switch. Open to managers, where `saveFlag` needs
     * an owner. Refused with 409 if somebody saved since `expectedVersion`.
     */
    toggleFlag(key, enabled, expectedVersion) {
        return this.request('PATCH', `/v1/flags/${encodeURIComponent(key)}/enabled`, {
            enabled,
            expectedVersion,
        });
    }
    /** Drops a flag's state so it serves its catalogue default. */
    resetFlag(key) {
        return this.request('DELETE', `/v1/flags/${encodeURIComponent(key)}`);
    }
    /** What a flag — or an unsaved draft of it — gives for one caller. */
    previewFlag(key, context, state) {
        return this.request('POST', `/v1/flags/${encodeURIComponent(key)}/preview`, { state, context });
    }
    flagHistory(key, take) {
        return this.request('GET', `/v1/flags/${encodeURIComponent(key)}/history${toQueryString({ take })}`);
    }
    // ── Audit trail ────────────────────────────────────────────
    /** Newest first. Pass the previous page's `nextCursor` as `before`. Manager and up. */
    listAudit(query = {}) {
        return this.request('GET', `/v1/audit${toQueryString({ ...query })}`);
    }
    /** Recomputes the whole hash chain. Owner only, and not cheap. */
    verifyAudit() {
        return this.request('POST', '/v1/audit/verify');
    }
    // ── Providers ──────────────────────────────────────────────
    /**
     * Adapters, the routing table and the resolved order. Pass a country or
     * currency to preview how routing resolves for it. Manager and up.
     */
    listProviders(scope = {}) {
        return this.request('GET', `/v1/providers${toQueryString(scope)}`);
    }
    /** Probes every configured provider. Slow-ish: one network call each. */
    providerHealth() {
        return this.request('GET', '/v1/providers/health');
    }
    /** Owner only. Refused with 409 if that provider already has a rule for that scope. */
    createProviderRoute(input) {
        return this.request('POST', '/v1/providers/routes', input);
    }
    /**
     * Edits a rule. Anything but `isEnabled` needs an owner. Refused with 409
     * if somebody saved since `patch.expectedVersion`.
     */
    updateProviderRoute(id, patch) {
        return this.request('PATCH', `/v1/providers/routes/${encodeURIComponent(id)}`, patch);
    }
    /** Switches a rule on or off. Open to managers — the kill switch for a failing provider. */
    toggleProviderRoute(id, isEnabled, expectedVersion) {
        return this.updateProviderRoute(id, { isEnabled, expectedVersion });
    }
    /** Owner only. With `expectedVersion`, refused with 409 if the rule changed since. */
    deleteProviderRoute(id, expectedVersion) {
        return this.request('DELETE', `/v1/providers/routes/${encodeURIComponent(id)}${toQueryString({ expectedVersion })}`);
    }
    /** Owner only. Sends one real message through exactly that provider, bypassing routing. */
    testProvider(input) {
        return this.request('POST', '/v1/providers/test', input);
    }
    // ── Settings ───────────────────────────────────────────────
    /**
     * Every tunable with the value in force for a scope.
     *
     * Returns the whole catalogue, not only what is stored, so the dashboard
     * can show a setting nobody has overridden alongside its default.
     */
    listSettings(scope = {}) {
        return this.request('GET', `/v1/settings${toQueryString(scope)}`);
    }
    saveSetting(input) {
        return this.request('PUT', '/v1/settings', input);
    }
    /** Drops an override so the scope falls back to a broader row or the default. */
    clearSetting(key, scope = {}) {
        return this.request('DELETE', `/v1/settings${toQueryString({ key, ...scope })}`);
    }
    // ── Partners ───────────────────────────────────────────────
    listPartners(query = {}) {
        return this.request('GET', `/v1/partners${toQueryString(query)}`);
    }
    getPartner(id) {
        return this.request('GET', `/v1/partners/${id}`);
    }
    /** Staff correcting a partner's details — including the two they cannot set themselves. */
    updatePartner(id, input) {
        return this.request('PATCH', `/v1/partners/${id}`, input);
    }
    reviewPartner(id, input) {
        return this.request('POST', `/v1/partners/${id}/review`, input);
    }
    myPartner() {
        return this.request('GET', '/v1/partners/me');
    }
    updateMyPartner(input) {
        return this.request('PATCH', '/v1/partners/me', input);
    }
    myPayoutDetails() {
        return this.request('GET', '/v1/partners/me/payout');
    }
    setMyPayoutDetails(input) {
        return this.request('PUT', '/v1/partners/me/payout', input);
    }
    myTeam() {
        return this.request('GET', '/v1/partners/me/team');
    }
    /** A partner's logins, for support and for the review screen. */
    partnerTeam(id) {
        return this.request('GET', `/v1/partners/${id}/team`);
    }
    /** Individuals Zuruflex's own account lists cars for. First party only. */
    carOwners(partnerId) {
        return this.request('GET', `/v1/partners/${partnerId}/car-owners`);
    }
    addCarOwner(partnerId, body) {
        return this.request('POST', `/v1/partners/${partnerId}/car-owners`, body);
    }
    updateCarOwner(partnerId, userId, body) {
        return this.request('PATCH', `/v1/partners/${partnerId}/car-owners/${userId}`, body);
    }
    /** Null unlinks the car, and its reservations go to the team alone. */
    setVehicleOwner(partnerId, vehicleId, ownerContactId) {
        return this.request('PUT', `/v1/partners/${partnerId}/vehicles/${vehicleId}/owner-contact`, {
            ownerContactId,
        });
    }
    setTeamRole(userId, role) {
        return this.request('PATCH', `/v1/partners/me/team/${userId}/role`, { role });
    }
    /**
     * Your own photograph, filed once the bytes are in S3.
     *
     * The key, never a URL: what a renter's browser is sent to is derived from
     * it on the server. There is no role behind this and no user id in it —
     * everybody has a face, and the only one you can change is your own.
     */
    setMyAvatar(key) {
        return this.request('PUT', '/v1/partners/me/avatar', { key });
    }
    /** Back to initials. The uploaded file is left where it is. */
    removeMyAvatar() {
        return this.request('DELETE', '/v1/partners/me/avatar');
    }
    // ── Onboarding and KYC ─────────────────────────────────────
    //
    // Both audiences read the same checklist from the same endpoint shape, so
    // what a partner is told is outstanding and what a reviewer is shown
    // outstanding are one answer rather than two.
    myOnboarding() {
        return this.request('GET', '/v1/partners/me/onboarding');
    }
    partnerOnboarding(id) {
        return this.request('GET', `/v1/partners/${id}/onboarding`);
    }
    myDocuments(vehicleId) {
        return this.request('GET', `/v1/partners/me/documents${toQueryString({ vehicleId })}`);
    }
    partnerDocuments(id, vehicleId) {
        return this.request('GET', `/v1/partners/${id}/documents${toQueryString({ vehicleId })}`);
    }
    /**
     * Presign, PUT the bytes straight to S3, then file the key — the same
     * three steps as a vehicle photo, and for the same reason: an ID card has
     * no business passing through the API process.
     *
     * `partnerId` only shapes the object key. The API takes whose document it
     * is from the token, so naming somebody else's id here gets a 403 rather
     * than a document filed against them.
     */
    async uploadPartnerDocument(partnerId, file, input) {
        if (file.size > MAX_UPLOAD_BYTES) {
            throw new ZuruflexApiError(413, {
                code: 'VALIDATION_ERROR',
                message: `${file.name} is larger than ${Math.floor(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`,
            });
        }
        const presigned = await this.request('POST', '/v1/uploads/presign', {
            kind: 'partner-document',
            ownerId: partnerId,
            documentKind: input.kind,
            filename: file.name,
            contentType: file.type,
            sizeBytes: file.size,
        });
        const doFetch = this.options.fetch ?? globalThis.fetch;
        const upload = await doFetch(presigned.uploadUrl, {
            method: 'PUT',
            // Pinned in the signature, so it has to match exactly.
            headers: { 'Content-Type': file.type },
            body: file,
        });
        if (!upload.ok) {
            throw new ZuruflexApiError(upload.status, {
                code: 'SERVICE_UNAVAILABLE',
                message: `Uploading ${file.name} failed`,
            });
        }
        return this.request('POST', '/v1/partners/me/documents', { ...input, key: presigned.key });
    }
    /**
     * The same three steps for a renter's licence: presign, PUT the bytes
     * straight to S3, file the key.
     *
     * `customerId` only shapes the object key. The API takes whose licence it
     * is from the token, so naming somebody else's id here gets a 403 rather
     * than a document filed against them.
     */
    async uploadCustomerDocument(customerId, file, input) {
        if (file.size > MAX_UPLOAD_BYTES) {
            throw new ZuruflexApiError(413, {
                code: 'VALIDATION_ERROR',
                message: `${file.name} is larger than ${Math.floor(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`,
            });
        }
        const presigned = await this.request('POST', '/v1/uploads/presign', {
            kind: 'customer-document',
            ownerId: customerId,
            customerDocumentKind: input.kind,
            filename: file.name,
            contentType: file.type,
            sizeBytes: file.size,
        });
        const doFetch = this.options.fetch ?? globalThis.fetch;
        const upload = await doFetch(presigned.uploadUrl, {
            method: 'PUT',
            // Pinned in the signature, so it has to match exactly.
            headers: { 'Content-Type': file.type },
            body: file,
        });
        if (!upload.ok) {
            throw new ZuruflexApiError(upload.status, {
                code: 'SERVICE_UNAVAILABLE',
                message: `Uploading ${file.name} failed`,
            });
        }
        return this.request('POST', '/v1/me/documents', { ...input, key: presigned.key });
    }
    /**
     * Your own licence, and what is still owed.
     *
     * Named for what it returns rather than for the route: the answer is the
     * checklist — a verdict plus a requirement per kind — and `myDocuments`
     * is already the partner's list of rows.
     */
    myVerification() {
        return this.request('GET', '/v1/me/documents');
    }
    /** Only one still awaiting review, or one that was sent back. */
    removeMyCustomerDocument(documentId) {
        return this.request('DELETE', `/v1/me/documents/${documentId}`);
    }
    /** Staff: a renter's licence, and what is still owed. */
    customerVerification(customerId) {
        return this.request('GET', `/v1/customers/${customerId}/documents`);
    }
    /** Staff: accept or send back one of them. */
    reviewCustomerDocument(customerId, documentId, body) {
        return this.request('POST', `/v1/customers/${customerId}/documents/${documentId}/review`, body);
    }
    removeMyDocument(documentId) {
        return this.request('DELETE', `/v1/partners/me/documents/${documentId}`);
    }
    /** Zuruflex's call on one document. Rejecting needs a note the partner can act on. */
    reviewPartnerDocument(partnerId, documentId, input) {
        return this.request('POST', `/v1/partners/${partnerId}/documents/${documentId}/review`, input);
    }
    /** "I am finished — please look at my account." */
    submitPartnerForReview() {
        return this.request('POST', '/v1/partners/me/submit');
    }
    // ── Terms and conditions ───────────────────────────────────
    /** Your own rental terms: what is live, what you are writing, what was. */
    myTerms() {
        return this.request('GET', '/v1/partners/me/terms');
    }
    /**
     * Save the working copy.
     *
     * `doc` is a ProseMirror document straight out of the editor. Nodes and
     * marks outside the allowed set come back as a 422 rather than being
     * quietly stripped — silently editing a legal document is worse than
     * refusing one.
     */
    saveMyTermsDraft(input) {
        return this.request('PUT', '/v1/partners/me/terms/draft', input);
    }
    /** Hand the working copy to Zuruflex. Owner only. */
    submitMyTerms() {
        return this.request('POST', '/v1/partners/me/terms/submit');
    }
    /** Every version of one partner's terms, for the dashboard. */
    partnerTerms(partnerId) {
        return this.request('GET', `/v1/partners/${partnerId}/terms`);
    }
    /** Zuruflex's call on one version. Sending it back needs a note the partner can act on. */
    reviewPartnerTerms(partnerId, termsId, input) {
        return this.request('POST', `/v1/partners/${partnerId}/terms/${termsId}/review`, input);
    }
    /**
     * The rental terms for a car, by the car's slug. Open to anyone.
     *
     * What the listing, the dates sheet and the conversation all link to: a
     * renter following it is looking at a car, and the address bar should
     * say so rather than naming the firm behind it.
     */
    vehicleTermsBySlug(slug) {
        return this.request('GET', `/v1/vehicles/by-slug/${encodeURIComponent(slug)}/terms`);
    }
    /** Where you stand on Zuruflex's partner agreement. */
    myPlatformTerms() {
        return this.request('GET', '/v1/partners/me/platform-terms');
    }
    /** Accept it, naming the version you were shown. Owner only. */
    acceptPlatformTerms(input) {
        return this.request('POST', '/v1/partners/me/platform-terms/accept', input);
    }
    /**
     * Whether you still have to agree to this car owner's terms.
     *
     * False both for an owner who has published none and for a version you
     * have already accepted, because a checkout draws the same thing either
     * way and the rule belongs on one side of the wire.
     */
    myPendingTerms(vehicleId) {
        return this.request('GET', `/v1/me/terms/pending${toQueryString({ vehicleId })}`);
    }
    // ── Fleet search ───────────────────────────────────────────
    /**
     * The customer-facing fleet search.
     *
     * Filters never appear in the address bar — the API answers with a
     * signed token that stands for them, and that token is the whole query
     * string. Searching and minting are one call, so changing a filter
     * costs one round trip rather than two.
     */
    searchFleet(filters) {
        return this.request('POST', '/v1/fleet/search', filters);
    }
    /** Reopens a search from its token — a link opened, pasted or shared. */
    openFleetSearch(token) {
        return this.request('GET', `/v1/fleet/search${toQueryString({ f: token })}`);
    }
    /**
     * The landing page's named rows, for one market.
     *
     * Each shelf carries a token of its own, so "show all" on a row is the
     * ordinary fleet search the row is a preview of — no second endpoint,
     * and no chance of the row and the page behind it disagreeing.
     */
    async fleetShelves(country) {
        const { shelves } = await this.request('GET', `/v1/fleet/shelves${toQueryString({ country })}`);
        return shelves;
    }
    // ── Badge performance ──────────────────────────────────────
    /**
     * How each listing badge is doing, shown arm against the holdout.
     *
     * Aggregate only — this reads a daily rollup, never a person. The site
     * sets no analytics cookie and needs no consent banner because there is
     * nothing here that could identify anybody.
     */
    badgeStats(query = {}) {
        return this.request('GET', `/v1/badges/stats${toQueryString(query)}`);
    }
    // ── Email templates ────────────────────────────────────────
    /** Every transactional email the API sends, subjects in `locale`. */
    emailTemplates(locale) {
        return this.request('GET', `/v1/email-templates${toQueryString({ locale })}`);
    }
    /** One email exactly as it would be sent, filled with sample values. */
    emailPreview(key, locale) {
        return this.request('GET', `/v1/email-templates/${encodeURIComponent(key)}${toQueryString({ locale })}`);
    }
    /** The filters behind a token, without running the search again. */
    async decodeFleetToken(token) {
        const { filters } = await this.request('GET', `/v1/fleet/filters${toQueryString({ f: token })}`);
        return filters;
    }
    // ── Reviews ────────────────────────────────────────────────
    /** A listing's revealed reviews, with the summary the page renders above them. */
    listingReviews(idOrSlug, query = {}) {
        return this.request('GET', `/v1/reviews/listing/${encodeURIComponent(idOrSlug)}${toQueryString(query)}`);
    }
    /** A guest's public page. 404s when they have opted out. */
    guestProfile(handle, query = {}) {
        return this.request('GET', `/v1/reviews/guest/${encodeURIComponent(handle)}${toQueryString(query)}`);
    }
    /**
     * What the review form needs, from the signed link.
     *
     * The token is the whole credential — there is no customer account to sign
     * in to — so it is passed on every call rather than exchanged for a session.
     */
    reviewInvite(token) {
        return this.request('GET', `/v1/reviews/invite${toQueryString({ t: token })}`);
    }
    submitReview(token, input) {
        return this.request('POST', `/v1/reviews/invite${toQueryString({ t: token })}`, input);
    }
    /**
     * The review link for one of the signed-in customer's own finished hires —
     * the token the concierge would have sent them over WhatsApp, for an app
     * that cannot intercept that message. Spend it on `reviewInvite` and
     * `submitReview` like any other.
     */
    myReviewInvite(reference) {
        return this.request('POST', `/v1/reviews/mine/${encodeURIComponent(reference)}`);
    }
    /** The owner's review of their guest, from the portal. */
    reviewGuest(reference, input) {
        return this.request('POST', `/v1/reviews/booking/${encodeURIComponent(reference)}`, input);
    }
    /**
     * A rating typed into the WhatsApp thread. Internal key only.
     *
     * The phone travels with it and is checked against the booking API-side:
     * holding the shared key is not the same as knowing whose hire this was.
     */
    recordWhatsappReview(input) {
        return this.request('POST', '/v1/reviews/whatsapp', input);
    }
    /** The words, when they arrive a message after the star. */
    appendWhatsappReviewBody(input) {
        return this.request('PATCH', '/v1/reviews/whatsapp/body', input);
    }
    /** The signed link for one side of a hire, for whoever will send it. */
    mintReviewInvite(reference, side = 'GUEST_ON_LISTING') {
        return this.request('POST', `/v1/reviews/invite/${encodeURIComponent(reference)}${toQueryString({ side })}`);
    }
    /** Staff moderation queue; a partner sees only reviews of their own cars. */
    listReviews(query = {}) {
        return this.request('GET', `/v1/reviews${toQueryString(query)}`);
    }
    /** Completed hires this partner still owes a guest review on. */
    pendingGuestReviews(query = {}) {
        return this.request('GET', `/v1/reviews/pending${toQueryString(query)}`);
    }
    moderateReview(id, input) {
        return this.request('PATCH', `/v1/reviews/${id}/moderation`, input);
    }
    reportReview(id, input) {
        return this.request('POST', `/v1/reviews/${id}/report`, input);
    }
    listReviewReports(query = {}) {
        return this.request('GET', `/v1/reviews/reports${toQueryString(query)}`);
    }
    resolveReviewReport(id, input) {
        return this.request('PATCH', `/v1/reviews/reports/${id}`, input);
    }
    // ── Vehicles ───────────────────────────────────────────────
    listVehicles(query = {}) {
        return this.request('GET', `/v1/vehicles${toQueryString(query)}`);
    }
    getVehicle(idOrSlug) {
        return this.request('GET', `/v1/vehicles/${encodeURIComponent(idOrSlug)}`);
    }
    createVehicle(input) {
        return this.request('POST', '/v1/vehicles', input);
    }
    /**
     * Reads a photographed registration document into Add-a-car fields.
     * Nothing is saved; behind the `fleet.scanRegistration` flag.
     */
    scanRegistration(input) {
        return this.request('POST', '/v1/vehicles/scan-registration', input);
    }
    updateVehicle(id, input) {
        return this.request('PATCH', `/v1/vehicles/${id}`, input);
    }
    /** Hands a car to another partner. Staff only. */
    transferVehicle(id, input) {
        return this.request('POST', `/v1/vehicles/${id}/transfer`, input);
    }
    retireVehicle(id) {
        return this.request('DELETE', `/v1/vehicles/${id}`);
    }
    // ── Getting a listing live ─────────────────────────────────
    submitListing(id) {
        return this.request('POST', `/v1/vehicles/${id}/submit`);
    }
    reviewListing(id, input) {
        return this.request('POST', `/v1/vehicles/${id}/review`, input);
    }
    unlistVehicle(id) {
        return this.request('POST', `/v1/vehicles/${id}/unlist`);
    }
    relistVehicle(id) {
        return this.request('POST', `/v1/vehicles/${id}/relist`);
    }
    // ── Photographs ────────────────────────────────────────────
    /**
     * The three steps of adding a photo, in one call: ask for a presigned
     * URL, PUT the bytes straight to S3, then record the key against the
     * listing. The file never passes through the API.
     */
    /**
     * Uploads a photograph and attaches it to the listing.
     *
     * `kind` is what the picture shows. It decides where the photo sits in the
     * gallery and whether it counts towards the set a listing needs to go
     * live, so it is worth passing even when the person picked the file from
     * a plain "add more" button.
     */
    async uploadVehicleImage(vehicleId, file, { kind, position, alt } = {}) {
        // Checked here so an oversized photo is a sentence rather than a 422
        // from a validator the person never sees.
        if (file.size > MAX_UPLOAD_BYTES) {
            throw new ZuruflexApiError(413, {
                code: 'VALIDATION_ERROR',
                message: `${file.name} is larger than ${Math.floor(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`,
            });
        }
        const presigned = await this.request('POST', '/v1/uploads/presign', {
            kind: 'vehicle-image',
            ownerId: vehicleId,
            filename: file.name,
            contentType: file.type,
            // The API pins the size into the signature, so it has to be sent.
            sizeBytes: file.size,
            position: position ?? 0,
        });
        const doFetch = this.options.fetch ?? globalThis.fetch;
        const upload = await doFetch(presigned.uploadUrl, {
            method: 'PUT',
            // Pinned in the signature, so it has to match exactly.
            headers: { 'Content-Type': file.type },
            body: file,
        });
        if (!upload.ok) {
            throw new ZuruflexApiError(upload.status, {
                code: 'SERVICE_UNAVAILABLE',
                message: `Uploading ${file.name} failed`,
            });
        }
        return this.request('POST', `/v1/vehicles/${vehicleId}/images`, {
            key: presigned.key,
            kind,
            alt,
            position,
        });
    }
    removeVehicleImage(vehicleId, imageId) {
        return this.request('DELETE', `/v1/vehicles/${vehicleId}/images/${imageId}`);
    }
    // ── Locations ──────────────────────────────────────────────
    /** Zuruflex branches, plus your own pick-up points if you are a partner. */
    listLocations() {
        return this.request('GET', '/v1/locations');
    }
    /** Your own branches, including ones switched off. Manager and above. */
    listMyLocations() {
        return this.request('GET', '/v1/locations/mine');
    }
    /** Open a branch of your own, in any country we price in. */
    createLocation(input) {
        return this.request('POST', '/v1/locations', input);
    }
    updateLocation(id, input) {
        return this.request('PATCH', `/v1/locations/${id}`, input);
    }
    /** Make this branch the one pre-selected when you list a car. */
    setDefaultLocation(id) {
        return this.request('POST', `/v1/locations/${id}/default`);
    }
    deleteLocation(id) {
        return this.request('DELETE', `/v1/locations/${id}`);
    }
    // ── The owner's own calendar ───────────────────────────────
    listBlackouts(vehicleId) {
        return this.request('GET', `/v1/vehicles/${vehicleId}/blackouts`);
    }
    addBlackout(vehicleId, input) {
        return this.request('POST', `/v1/vehicles/${vehicleId}/blackouts`, input);
    }
    removeBlackout(vehicleId, blackoutId) {
        return this.request('DELETE', `/v1/vehicles/${vehicleId}/blackouts/${blackoutId}`);
    }
    searchVehicles(query) {
        return this.request('GET', `/v1/vehicles/search${toQueryString(query)}`);
    }
    checkAvailability(vehicleId, pickupAt, dropoffAt) {
        return this.request('GET', `/v1/vehicles/${vehicleId}/availability${toQueryString({ pickupAt, dropoffAt })}`);
    }
    // ── Quotes & bookings ──────────────────────────────────────
    quote(input) {
        return this.request('POST', '/v1/quotes', input);
    }
    /**
     * Staff and the concierge booking for somebody. Takes `promoCode` like a
     * quote does — but a code that does not apply refuses the booking with a
     * `promotion.<reason>` error rather than pricing it at full fare.
     */
    createBooking(input) {
        return this.request('POST', '/v1/bookings', input);
    }
    getBooking(reference) {
        return this.request('GET', `/v1/bookings/${encodeURIComponent(reference)}`);
    }
    listBookings(query = {}) {
        return this.request('GET', `/v1/bookings${toQueryString(query)}`);
    }
    updateBookingStatus(id, status, reason) {
        return this.request('PATCH', `/v1/bookings/${id}/status`, { status, reason });
    }
    /**
     * Staff found a hand-sent MoMo payment in the account. Marks it paid,
     * confirms the booking and tells the customer. `amount` defaults to
     * what was due.
     */
    approveManualPayment(reference, input) {
        return this.request('POST', `/v1/bookings/${encodeURIComponent(reference)}/payments/manual`, input);
    }
    // ── The handover, and the return check ─────────────────────
    //
    // What the owner records when the keys change hands, in both
    // directions. All four are staff-and-partner, and a partner only ever
    // reaches their own bookings.
    /** Both ends of the hire, oldest first. At most two. */
    listBookingHandovers(reference) {
        return this.request('GET', `/v1/bookings/${encodeURIComponent(reference)}/handovers`);
    }
    /**
     * Records one end of the hire, or corrects a record that is still open.
     *
     * Recording does not move the booking's status: call
     * `updateBookingStatus` straight afterwards — ACTIVE after a pick-up,
     * COMPLETED after a return — which is the moment what was just written
     * is sealed, and the moment the renter is told neither side can change
     * it. A second call before that is a correction, not a second handover.
     */
    recordBookingHandover(reference, input) {
        return this.request('POST', `/v1/bookings/${encodeURIComponent(reference)}/handovers`, input);
    }
    /**
     * Step one of the same three-step dance as a vehicle photo: a
     * short-lived PUT URL the bytes go straight to.
     *
     * `publicUrl` is always null here. Handover photographs live under
     * `private/` because they are evidence in a deposit dispute, so what
     * `listBookingHandovers` hands back is a signed URL minted at read
     * time rather than a permanent one. The field is present so the shape
     * matches `/v1/uploads/presign`, which the partner app re-runs for
     * React Native.
     */
    handoverPhotoUploadUrl(reference, input) {
        return this.request('POST', `/v1/bookings/${encodeURIComponent(reference)}/handovers/photo-url`, input);
    }
    /** Step three: file the key, once the bytes are in S3. */
    attachHandoverPhoto(reference, input) {
        return this.request('POST', `/v1/bookings/${encodeURIComponent(reference)}/handovers/photos`, input);
    }
    // ── Customers ──────────────────────────────────────────────
    /** Staff only. `q` matches a name, phone number or email. */
    listCustomers(query = {}) {
        return this.request('GET', `/v1/customers${toQueryString(query)}`);
    }
    findOrCreateCustomerByPhone(phone, fullName) {
        return this.request('POST', '/v1/customers/by-phone', { phone, fullName });
    }
    /** What the concierge remembers about a customer between chats. Internal. */
    chatProfile(customerId) {
        return this.request('GET', `/v1/customers/${encodeURIComponent(customerId)}/chat-profile`);
    }
    /** Keeps what a customer told the concierge on their record. Internal. */
    updateChatProfile(customerId, input) {
        return this.request('PATCH', `/v1/customers/${encodeURIComponent(customerId)}/chat-profile`, input);
    }
    /*
     * ── Saved cars ─────────────────────────────────────────────
     *
     * All customer-only, and all scoped to the signed-in account, so none
     * of them names whose list it is.
     */
    /**
     * The saved cars themselves, most recently saved first.
     *
     * `unavailable` counts the ones that did not come back — unlisted since
     * they were saved, or their partner suspended. They are still on the
     * account; they are just not showable today.
     */
    listSaved() {
        return this.request('GET', '/v1/saved');
    }
    /** Just the ids — what the fleet grid needs to fill in its hearts. */
    async savedIds() {
        const { ids } = await this.request('GET', '/v1/saved/ids');
        return ids;
    }
    /** Idempotent: saving a car already saved succeeds and changes nothing. */
    saveVehicle(vehicleId) {
        return this.request('PUT', `/v1/saved/${encodeURIComponent(vehicleId)}`);
    }
    /** Also idempotent — removing one that is not saved succeeds. */
    unsaveVehicle(vehicleId) {
        return this.request('DELETE', `/v1/saved/${encodeURIComponent(vehicleId)}`);
    }
    /**
     * Hands over the list the browser was holding before there was an
     * account to keep it on, and returns the resulting ids. Unknown or
     * unavailable cars are skipped rather than rejected.
     */
    async importSaved(vehicleIds) {
        const { ids } = await this.request('POST', '/v1/saved/import', {
            vehicleIds,
        });
        return ids;
    }
    /*
     * ── My bookings ────────────────────────────────────────────
     *
     * All customer-only, all scoped to the signed-in account, so none of
     * them names whose booking it is. `POST /v1/quotes` stays the way a
     * price is discovered — these never compute one.
     */
    /** Requests a car. Creates a PENDING booking; nothing is charged. */
    requestBooking(input) {
        return this.request('POST', '/v1/me/bookings', input);
    }
    /**
     * Offers this renter can use: codes assigned to them — referral rewards
     * among them — and the automatic promos that apply themselves.
     */
    myPromotions() {
        return this.request('GET', '/v1/me/promotions');
    }
    /**
     * Their referral code, minted on first ask, with a share link and how it
     * has done. Terms are for the market asked about, else where they last
     * hired. `enabled: false` means there is no code to share there.
     */
    myReferral(scope = {}) {
        return this.request('GET', `/v1/me/referral${toQueryString(scope)}`);
    }
    myBookings(scope = 'ALL') {
        return this.request('GET', `/v1/me/bookings${toQueryString({ scope })}`);
    }
    myBooking(reference) {
        return this.request('GET', `/v1/me/bookings/${encodeURIComponent(reference)}`);
    }
    /**
     * What cancelling would cost this second.
     *
     * Read from the server rather than worked out here: the fee depends on
     * how long until pick-up, and a device clock that is off — or a dialog
     * left open across the 24-hour boundary — would promise "free" against
     * a server about to charge a quarter.
     */
    cancellationQuote(reference) {
        return this.request('GET', `/v1/me/bookings/${encodeURIComponent(reference)}/cancellation-quote`);
    }
    /** Pass the fee that was shown; a stale one is refused, not charged. */
    cancelMyBooking(reference, input = {}) {
        return this.request('POST', `/v1/me/bookings/${encodeURIComponent(reference)}/cancel`, input);
    }
    /** Pass the total that was shown; a price that moved is refused. */
    changeMyBookingDates(reference, input) {
        return this.request('PATCH', `/v1/me/bookings/${encodeURIComponent(reference)}/dates`, input);
    }
    /**
     * Starts a hosted checkout and hands back the page to send the browser
     * to. `AT_BRANCH` means no gateway can settle this currency online and
     * the customer pays at the counter — a real outcome, not a failure.
     */
    startCheckout(reference) {
        return this.request('POST', `/v1/me/payments/${encodeURIComponent(reference)}/checkout`);
    }
    myPaymentStatus(reference) {
        return this.request('GET', `/v1/me/payments/${encodeURIComponent(reference)}`);
    }
    /**
     * Asks the gateway directly, on the way back from its page.
     *
     * A latency optimisation only — the webhook settles the payment whether
     * or not anybody returns — so a failure here is not worth reporting.
     */
    verifyPayment(reference) {
        return this.request('POST', `/v1/me/payments/${encodeURIComponent(reference)}/verify`);
    }
    /*
     * ── Threads ────────────────────────────────────────────────
     *
     * One set of routes for both kinds of conversation, and one for all
     * three realms: a customer sees their own, a partner their team's, staff
     * everybody's. The slicing happens on the API, so no front-end holds a
     * rule about who may read what.
     */
    /** The inbox, most recently spoken in first. */
    listThreads(query = {}) {
        return this.request('GET', `/v1/threads${toQueryString(query)}`);
    }
    /** One number, for the nav badge. Cheap enough to ask for on every page. */
    async unreadMessages() {
        const { count } = await this.request('GET', '/v1/threads/unread');
        return count;
    }
    thread(id) {
        return this.request('GET', `/v1/threads/${encodeURIComponent(id)}`);
    }
    /**
     * Opens the conversation these two have, pointed at the hire in hand.
     *
     * A customer and a partner have one thread however many times they do
     * business, so this is safe to call from a Message button on any booking
     * row and from one somebody taps twice. Without a `body` it writes
     * nothing — it just moves the head and hands back the thread.
     */
    openThread(input) {
        return this.request('POST', '/v1/threads', input);
    }
    /**
     * Everything after `since`, in order.
     *
     * `messageSeq` comes back alongside and is the *thread's* high-water
     * mark, not the last row's: a staff note advances it invisibly, and a
     * client that used the last message it could see would ask for the same
     * gap on every poll.
     */
    threadMessages(id, query = {}) {
        return this.request('GET', `/v1/threads/${encodeURIComponent(id)}/messages${toQueryString(query)}`);
    }
    /**
     * The signed URL for one attachment, in a body.
     *
     * `/v1/threads/attachments/:id` answers a 302, which is right for a link
     * in a browser and useless to a native image loader: React Native's fetch
     * follows the redirect before any JavaScript can read the `Location`, and
     * the header it would have carried does not survive the hop to storage on
     * either platform.
     *
     * One round trip for one picture, at the moment somebody opens it. What a
     * *list* draws travels on the thread instead, as `thumbnailUrl`.
     */
    attachmentLink(attachmentId) {
        return this.request('GET', `/v1/threads/attachments/${encodeURIComponent(attachmentId)}/link`);
    }
    /**
     * One thread as it happens, until the returned function is called.
     *
     * The counterpart to `thread()`, not a replacement for it: this carries
     * messages only, past the `since` cursor, and the thread's own details
     * still come from the ordinary read. A client opens the thread once and
     * then listens.
     *
     * It is a **tail, not the transport of record**. Every message here is a
     * row already committed, so a stream that drops has cost promptness and
     * nothing else — reconnect with the last `seq` drawn and read on. That is
     * also why the caller is handed `onClose` rather than a reconnect loop:
     * only the caller knows whether anybody is still looking at the screen.
     *
     * The token rides in a header, which is the reason this cannot be an
     * `EventSource` — see `stream.ts`.
     */
    streamThread(threadId, handlers, since = 0) {
        const token = this.currentToken();
        const locale = this.currentLocale();
        return openEventStream(`${this.baseUrl}/v1/threads/${encodeURIComponent(threadId)}/stream${toQueryString({ since })}`, {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(locale ? { 'Accept-Language': locale } : {}),
        }, {
            onEvent: (event) => {
                // Pings keep the socket warm and say nothing; everything else is
                // a message, and a payload that will not parse is a bug on the
                // wire rather than something to guess at.
                if (event.type !== 'message' && event.type !== 'revise')
                    return;
                try {
                    const message = JSON.parse(event.data);
                    if (event.type === 'revise')
                        handlers.onRevise?.(message);
                    else
                        handlers.onMessage(message);
                }
                catch {
                    // Ignored: the next read will bring it properly.
                }
            },
            onClose: handlers.onClose,
        });
    }
    /** Contact details are taken out until the hire is confirmed; `wasRedacted` says whether they were. */
    sendMessage(id, input) {
        return this.request('POST', `/v1/threads/${encodeURIComponent(id)}/messages`, input);
    }
    /**
     * Puts a file in a thread's prefix and says what to attach it as.
     *
     * The bytes go straight to S3, as every other upload here does — an 8MB
     * photograph of a bumper has no business occupying an API process. The
     * key comes back and travels with the next message; nothing is attached
     * to anything until that message is sent, so an abandoned pick leaves an
     * orphan object and no row.
     */
    async uploadThreadAttachment(threadId, file) {
        if (file.size > MAX_UPLOAD_BYTES) {
            throw new ZuruflexApiError(413, {
                code: 'VALIDATION_ERROR',
                message: `${file.name} is larger than ${Math.floor(MAX_UPLOAD_BYTES / 1024 / 1024)}MB`,
            });
        }
        /*
         * No `withThumbnail`: a browser has not decoded this file and is not
         * going to, so there is nothing to put in a second place. The answer
         * carries a null thumbnail and this method returns exactly what it
         * always has — which is the point, because three portals call it.
         */
        const presigned = await this.request('POST', `/v1/threads/${encodeURIComponent(threadId)}/attachments`, { filename: file.name, contentType: file.type, sizeBytes: file.size });
        const doFetch = this.options.fetch ?? globalThis.fetch;
        const upload = await doFetch(presigned.uploadUrl, {
            method: 'PUT',
            // Pinned in the signature, so it has to match exactly.
            headers: { 'Content-Type': file.type },
            body: file,
        });
        if (!upload.ok) {
            throw new ZuruflexApiError(upload.status, {
                code: 'SERVICE_UNAVAILABLE',
                message: `Uploading ${file.name} failed`,
            });
        }
        return {
            key: presigned.key,
            filename: file.name,
            contentType: file.type,
            sizeBytes: file.size,
        };
    }
    /**
     * Changes the body of one of the caller's own messages. Answers with the
     * message as stored — which may be masked, so draw that, not the draft.
     */
    editMessage(threadId, messageId, input) {
        return this.request('PATCH', `/v1/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}`, input);
    }
    /** Withdraws one of the caller's own messages. Soft; see the API. */
    deleteMessage(threadId, messageId) {
        return this.request('DELETE', `/v1/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}`);
    }
    /** Stops (or restarts) this thread waking the caller. Theirs alone, not the thread's. */
    muteThread(id, muted) {
        return this.request('POST', `/v1/threads/${encodeURIComponent(id)}/mute`, { muted });
    }
    /** Forward only, and clamped to the thread. */
    markThreadRead(id, seq) {
        return this.request('POST', `/v1/threads/${encodeURIComponent(id)}/read`, { seq });
    }
    /** Status, assignee, priority, topic, tags. Staff only. */
    updateThread(id, input) {
        return this.request('PATCH', `/v1/threads/${encodeURIComponent(id)}`, input);
    }
    /**
     * The same patch, across a selection. Answers per thread, not all or
     * nothing: a thread a colleague resolved thirty seconds ago refuses the
     * transition, and the other ninety-nine should still move. A caller that
     * fired a PATCH each would have to reassemble that itself.
     */
    bulkUpdateThreads(input) {
        return this.request('PATCH', '/v1/threads/bulk', input);
    }
    /**
     * How the desk is doing over a window, for the reporting page.
     *
     * Percentiles rather than means, computed on the API: the numbers are
     * drawn from every thread in the range and not from the page of them a
     * dashboard happens to be holding.
     */
    threadMetrics(range = {}) {
        return this.request('GET', `/v1/threads/metrics${toQueryString(range)}`);
    }
    /**
     * Wakes snoozed threads that are due and closes resolved ones nobody
     * came back to. Behind the internal key and driven by cron, like
     * `sweepBookingHolds` — there is no scheduler in the API.
     */
    sweepThreads() {
        return this.request('POST', '/v1/threads/sweep');
    }
    /**
     * The score the person who wrote in gives the help they got.
     *
     * Theirs alone, and only once: staff would be rating their own desk, and
     * a renter asked twice about one thread answers neither time. The client
     * does not decide when to offer it — a thread carries `csat` once the ask
     * has gone out, and null until then.
     */
    submitCsat(id, input) {
        return this.request('POST', `/v1/threads/${encodeURIComponent(id)}/csat`, input);
    }
    /*
     * ── The desk's two lists ───────────────────────────────────
     *
     * Who a thread can be handed to, and what can be said in one. Both are
     * staff-only, both are read far more often than they change, and both
     * are here rather than under admin because the inbox is their caller.
     */
    /**
     * Colleagues, by name — the assignee picker, and the names a per-agent
     * report puts against its rows.
     *
     * People who have left come back too when asked for: a picker must not
     * offer them, while a report covering last quarter still has to name the
     * person who resolved forty threads in it.
     */
    listStaff(query = {}) {
        return this.request('GET', `/v1/staff${toQueryString(query)}`);
    }
    /**
     * The canned answers an agent can reach for, for the language they are
     * answering in.
     *
     * A topic widens rather than narrows: asking for PAYMENT also brings back
     * the replies filed under no topic, because a greeting belongs in every
     * conversation. Retired wordings stay out unless the screen that
     * maintains them asks.
     */
    listSavedReplies(query = {}) {
        return this.request('GET', `/v1/saved-replies${toQueryString(query)}`);
    }
    createSavedReply(input) {
        return this.request('POST', '/v1/saved-replies', input);
    }
    /** Send only what changed. `topic: null` files it back under "any topic". */
    updateSavedReply(id, input) {
        return this.request('PATCH', `/v1/saved-replies/${encodeURIComponent(id)}`, input);
    }
    /**
     * Retires one rather than deleting it — what the team was telling people
     * last quarter is most of what makes a complaint answerable six months
     * later. It leaves the picker at once, and `updateSavedReply(id, {
     * isActive: true })` brings it back.
     */
    deleteSavedReply(id) {
        return this.request('DELETE', `/v1/saved-replies/${encodeURIComponent(id)}`);
    }
    /*
     * ── Promotions ─────────────────────────────────────────────
     *
     * Staff and partners, from the same routes: a partner reads and writes
     * only their own, always at their own cost, and somebody else's promo
     * is a 404. Generating codes and naming customers are staff-only.
     */
    listPromotions(query = {}) {
        return this.request('GET', `/v1/promotions${toQueryString(query)}`);
    }
    /** One promo, with what it has cost and brought in. */
    getPromotion(id) {
        return this.request('GET', `/v1/promotions/${encodeURIComponent(id)}`);
    }
    createPromotion(input) {
        return this.request('POST', '/v1/promotions', input);
    }
    /** Send only what changed; `null` clears an optional limit. */
    updatePromotion(id, input) {
        return this.request('PATCH', `/v1/promotions/${encodeURIComponent(id)}`, input);
    }
    /** Archives rather than deletes: bookings that used it still point at it. */
    archivePromotion(id) {
        return this.request('DELETE', `/v1/promotions/${encodeURIComponent(id)}`);
    }
    pausePromotion(id) {
        return this.request('POST', `/v1/promotions/${encodeURIComponent(id)}/pause`);
    }
    resumePromotion(id) {
        return this.request('POST', `/v1/promotions/${encodeURIComponent(id)}/resume`);
    }
    promotionRedemptions(id, query = {}) {
        return this.request('GET', `/v1/promotions/${encodeURIComponent(id)}/redemptions${toQueryString(query)}`);
    }
    /** Staff only. Single-use copies of this promo's terms, one code each. */
    generatePromotionCodes(id, input) {
        return this.request('POST', `/v1/promotions/${encodeURIComponent(id)}/codes`, input);
    }
    /** Staff only, ASSIGNED promos only. Adding somebody twice is not an error. */
    assignPromotion(id, customerIds) {
        return this.request('POST', `/v1/promotions/${encodeURIComponent(id)}/assignments`, {
            customerIds,
        });
    }
    /*
     * ── The concierge's two doors ──────────────────────────────
     *
     * Service-to-service, behind the internal key. Here rather than as a
     * `fetch` in the concierge for the reason every other route is here:
     * one place to change the shape, and the bot cannot drift from what the
     * API accepts.
     */
    /** A customer on WhatsApp has asked for a person. Idempotent per conversation. */
    openHandoffThread(input) {
        return this.request('POST', '/v1/threads/internal/handoff', input);
    }
    /**
     * An inbound WhatsApp message on a conversation a human already holds.
     *
     * `accepted: false` means there is no handed-off thread and the bot
     * still has it — not an error, and the concierge carries on.
     */
    forwardInboundMessage(input) {
        return this.request('POST', '/v1/threads/internal/inbound', input);
    }
    // ── Web chat ───────────────────────────────────────────────
    //
    // The concierge on the website. The session call is the only one that
    // looks at the Bearer token — a signed-in customer's chat attaches to
    // their account, a guest gets a customer of their own — and after that
    // the chat token is the whole credential, for everyone. It goes as a
    // header rather than through `token`, and past the refresh-and-retry
    // path: a rejected chat token is not an expired access token, and a new
    // access token cannot change the answer. Nor does the Bearer ride along
    // (the empty override token below): the API's auth guard checks any
    // Bearer it is offered, on public routes too, so a signed-in customer
    // whose access token had quietly expired would get a 401 the widget reads
    // as a dead chat token — and lose the conversation on screen with it.
    /** Opens (or reopens) the caller's web conversation and issues its token. */
    startChat(input = {}) {
        return this.request('POST', '/v1/chat/session', input);
    }
    /**
     * Hands a message to the concierge. Answers once it is recorded; the
     * reply arrives on the stream, not here, because the flow may take
     * seconds and may say several things.
     */
    sendChatMessage(token, input) {
        return this.send('POST', '/v1/chat/messages', input, '', { 'x-chat-token': token });
    }
    /** The conversation after `since` (a `seq`), oldest first — the history on open. */
    chatMessages(token, query = {}) {
        return this.send('GET', `/v1/chat/messages${toQueryString(query)}`, undefined, '', {
            'x-chat-token': token,
        });
    }
    /**
     * Where to point an `EventSource`. The token rides in the query because
     * the browser's `EventSource` cannot set a header; `since` is only for
     * the first connection, since a reconnect sends `Last-Event-ID` itself.
     */
    chatStreamUrl(token, since) {
        return `${this.baseUrl}/v1/chat/stream${toQueryString({ t: token, since })}`;
    }
    /**
     * The same stream, for a runtime with no `EventSource` — the phones.
     *
     * Read through `openEventStream`, so the token can go as the header the
     * JSON routes take rather than in the URL. There is no `Last-Event-ID`
     * here: a reconnect is a new request, and the caller passes the last
     * `seq` it drew as `since`. Pings and anything that will not parse are
     * dropped; the next catch-up read brings whatever they would have.
     */
    streamChat(token, handlers, since = 0) {
        return openEventStream(`${this.baseUrl}/v1/chat/stream${toQueryString({ since })}`, { 'x-chat-token': token }, {
            onEvent: (event) => {
                if (event.type !== 'message' && event.type !== 'typing' && event.type !== 'handoff') {
                    return;
                }
                try {
                    const parsed = JSON.parse(event.data);
                    if (parsed && typeof parsed.type === 'string')
                        handlers.onEvent(parsed);
                }
                catch {
                    // Ignored: the next catch-up read brings it properly.
                }
            },
            onClose: handlers.onClose,
        });
    }
    // ── Push notifications ─────────────────────────────────────
    //
    // The two phone apps. The portals are a browser tab somebody has open
    // already; these exist because a car owner is standing next to a car,
    // or a renter is at an airport, with the app closed.
    //
    // Two sets of methods against two sets of routes, rather than one set
    // that reads the realm off the session. The `my` prefix is the same one
    // `myBookings` uses and means the same thing — the person holding this
    // token — and keeping them apart means a partner screen cannot
    // accidentally compile against a customer route.
    /**
     * Says where this handset can be reached.
     *
     * Idempotent, and meant to be called on every launch rather than once
     * when permission is granted: Expo rotates tokens on reinstall, on a
     * restore from backup and at the OS’s discretion, so an app that
     * registers once eventually goes quiet with nothing to show for it.
     */
    registerPushDevice(input) {
        return this.request('POST', '/v1/push/devices', input);
    }
    pushDevices() {
        return this.request('GET', '/v1/push/devices');
    }
    /** Sent on sign-out, so a shared counter phone stops ringing for you. */
    unregisterPushDevice(token) {
        return this.request('DELETE', `/v1/push/devices/${encodeURIComponent(token)}`);
    }
    pushPreferences() {
        return this.request('GET', '/v1/push/preferences');
    }
    /** A patch: one switch sends one field. */
    setPushPreferences(input) {
        return this.request('PATCH', '/v1/push/preferences', input);
    }
    // ── Notifications ──────────────────────────────────────────
    /**
     * What a partner was told, whether or not a phone was listening.
     *
     * Not the same list as "what we sent": a muted channel is a quiet
     * handset, not news that did not happen, so this includes both.
     */
    notifications(query = {}) {
        return this.request('GET', `/v1/notifications${toQueryString(query)}`);
    }
    /** The dot on the bell. Its own route because it is asked for far more often. */
    unreadNotifications() {
        return this.request('GET', '/v1/notifications/unread');
    }
    markNotificationRead(id) {
        return this.request('PATCH', `/v1/notifications/${id}/read`);
    }
    /** What opening the screen does: the dot goes out, the rows stay. */
    markNotificationsRead() {
        return this.request('POST', '/v1/notifications/read');
    }
    // ── The renter's half of both ──────────────────────────────
    //
    // Identical shapes against `me/`. See the note above for why these are
    // separate methods rather than one set that branches on the session.
    /**
     * Says where this renter's handset can be reached.
     *
     * Called on every launch for the same reason the partner's is: Expo
     * rotates tokens, and an app that registers once goes quiet months
     * later with nothing to show for it.
     */
    registerMyPushDevice(input) {
        return this.request('POST', '/v1/me/push/devices', input);
    }
    myPushDevices() {
        return this.request('GET', '/v1/me/push/devices');
    }
    /** Sent on sign-out, so a borrowed phone stops ringing for you. */
    unregisterMyPushDevice(token) {
        return this.request('DELETE', `/v1/me/push/devices/${encodeURIComponent(token)}`);
    }
    myPushPreferences() {
        return this.request('GET', '/v1/me/push/preferences');
    }
    /** A patch: one switch sends one field. */
    setMyPushPreferences(input) {
        return this.request('PATCH', '/v1/me/push/preferences', input);
    }
    /**
     * What a renter was told, whether or not a phone was listening.
     *
     * Fills far more slowly than a partner's — a couple of hires a year is
     * a handful of rows. That is the point: it is where a notification
     * missed on a lock screen is still waiting, not something to scroll.
     */
    myNotifications(query = {}) {
        return this.request('GET', `/v1/me/notifications${toQueryString(query)}`);
    }
    myUnreadNotifications() {
        return this.request('GET', '/v1/me/notifications/unread');
    }
    markMyNotificationRead(id) {
        return this.request('PATCH', `/v1/me/notifications/${id}/read`);
    }
    /** What opening the screen does: the dot goes out, the rows stay. */
    markMyNotificationsRead() {
        return this.request('POST', '/v1/me/notifications/read');
    }
    // ── Plumbing ───────────────────────────────────────────────
    /**
     * One 401 is expected rather than exceptional: access tokens last fifteen
     * minutes, so any session left open over lunch meets one. It is handled
     * here — refresh, retry once — so no caller has to think about it. A
     * second 401 is a real one and propagates.
     */
    async request(method, path, body, headers) {
        try {
            return await this.send(method, path, body, undefined, headers);
        }
        catch (error) {
            if (!(error instanceof ZuruflexApiError) || error.status !== 401)
                throw error;
            const refreshed = await this.options.onUnauthorized?.();
            if (!refreshed)
                throw error;
            return this.send(method, path, body, refreshed, headers);
        }
    }
    async send(method, path, body, overrideToken, extraHeaders) {
        const doFetch = this.options.fetch ?? globalThis.fetch;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), this.options.timeoutMs ?? 10_000);
        const headers = { Accept: 'application/json' };
        if (body !== undefined)
            headers['Content-Type'] = 'application/json';
        if (this.options.internalKey)
            headers['x-internal-key'] = this.options.internalKey;
        const token = overrideToken ?? this.currentToken();
        if (token)
            headers.Authorization = `Bearer ${token}`;
        const locale = this.currentLocale();
        if (locale)
            headers['x-lang'] = locale;
        const { app } = this.options;
        if (app) {
            headers['x-app-name'] = app.name;
            headers['x-app-version'] = app.version;
            headers['x-app-platform'] = app.platform;
            if (app.build)
                headers['x-app-build'] = app.build;
            if (app.variant)
                headers['x-app-variant'] = app.variant;
        }
        if (extraHeaders)
            Object.assign(headers, extraHeaders);
        let response;
        try {
            response = await doFetch(`${this.baseUrl}${path}`, {
                method,
                headers,
                body: body === undefined ? undefined : JSON.stringify(body),
                signal: controller.signal,
            });
        }
        catch (cause) {
            const aborted = cause instanceof Error && cause.name === 'AbortError';
            throw new ZuruflexApiError(0, {
                code: aborted ? 'SERVICE_UNAVAILABLE' : 'INTERNAL_ERROR',
                message: aborted ? `Request to ${path} timed out` : `Request to ${path} failed`,
            });
        }
        finally {
            clearTimeout(timeout);
        }
        /**
         * A deleted blackout, a sign-out, a changed password: routes whose
         * answer is "done" send 204 and no body at all. Parsing that as JSON
         * yields null, which used to surface as "malformed response" — a
         * successful call reported as a failure.
         */
        if (response.status === 204 || response.headers.get('content-length') === '0') {
            if (response.ok)
                return undefined;
            throw new ZuruflexApiError(response.status, {
                code: 'INTERNAL_ERROR',
                message: `Request to ${path} failed with ${response.status}`,
            });
        }
        const payload = (await response.json().catch(() => null));
        if (!payload) {
            throw new ZuruflexApiError(response.status, {
                code: 'INTERNAL_ERROR',
                message: `Malformed response from ${path}`,
            });
        }
        if (!payload.ok)
            throw new ZuruflexApiError(response.status, payload.error);
        return payload.data;
    }
}
function toQueryString(params) {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '')
            continue;
        if (Array.isArray(value)) {
            for (const item of value)
                search.append(key, String(item));
        }
        else {
            search.set(key, String(value));
        }
    }
    const qs = search.toString();
    return qs ? `?${qs}` : '';
}
